import * as THREE from "three";

/**
 * Layer hotspot HTML ancorato alla scena (cilindro).
 * hotspots: [{ id, lon, lat, text?, className?, css_classes?, label? }]
 */
export function createHotspotLayer({
  containerEl,
  camera,
  radius,
  lookOffsetDeg = 90,
  centerOffsetDeg = 0,
  hotspots = [],
  dotClass = "hs-dot",
  onHotspotClick = null,
}) {
  if (!containerEl) throw new Error("createHotspotLayer: containerEl mancante");
  if (!camera) throw new Error("createHotspotLayer: camera mancante");
  if (!radius) throw new Error("createHotspotLayer: radius mancante");

  const computedPos = window.getComputedStyle(containerEl).position;
  if (computedPos === "static") containerEl.style.position = "relative";

  const rr = radius - 1;
  const map = new Map(); // id -> { data, el }

  function pointOnCylinder(lonDeg, latDeg) {
    const theta = THREE.MathUtils.degToRad(lonDeg + lookOffsetDeg + centerOffsetDeg);
    const phi = THREE.MathUtils.degToRad(90 - latDeg);

    return new THREE.Vector3(
      rr * Math.sin(phi) * Math.cos(theta),
      rr * Math.cos(phi),
      rr * Math.sin(phi) * Math.sin(theta)
    );
  }

  function makeDot(hs) {
    const el = document.createElement("div");
    const extraClasses = hs.css_classes || hs.className || "";
    el.className = `${dotClass}${extraClasses ? ` ${extraClasses}` : ""}`;

    el.dataset.hotspotId = hs.id;
    if (hs.text != null) el.dataset.text = hs.text;

    el.dataset.allowedToolIds = (hs.pulse_tool_ids || []).join(",");

    el.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      e.stopPropagation();
    });

    el.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (typeof onHotspotClick === "function") {
        onHotspotClick({
          event: e,
          hotspot: map.get(hs.id)?.data || hs,
          element: el,
        });
      }
    });

    if (hs.label != null) {
      const lab = document.createElement("div");
      lab.className = "hs-dot__label";
      lab.textContent = String(hs.label);
      el.appendChild(lab);
    }

    containerEl.appendChild(el);
    return el;
  }

  function add(hs) {
    if (!hs?.id) throw new Error("Hotspot senza id");
    if (map.has(hs.id)) return map.get(hs.id).el;

    const el = makeDot(hs);
    map.set(hs.id, { data: { ...hs }, el });
    return el;
  }

  function remove(id) {
    const item = map.get(id);
    if (!item) return;
    item.el.remove();
    map.delete(id);
  }

  function setHotspots(list) {
    const ids = new Set(list.map((h) => h.id));

    for (const id of map.keys()) {
      if (!ids.has(id)) remove(id);
    }

    list.forEach((hs) => {
      if (!map.has(hs.id)) {
        add(hs);
      } else {
        const item = map.get(hs.id);
        item.data = { ...item.data, ...hs };

        if (hs.text != null) item.el.dataset.text = hs.text;
        item.el.dataset.allowedToolIds = (hs.pulse_tool_ids || []).join(",");
      }
    });
  }

  function updateOne(item) {
    const { data, el } = item;
    const world = pointOnCylinder(data.lon, data.lat);
    const v = world.clone().project(camera);

    if (v.z > 1) {
      el.style.display = "none";
      return;
    }

    const x = (v.x * 0.5 + 0.5) * containerEl.clientWidth;
    const y = (-v.y * 0.5 + 0.5) * containerEl.clientHeight;

    el.style.display = "block";
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }

  function update() {
    for (const item of map.values()) updateOne(item);
  }

  function destroy() {
    for (const id of Array.from(map.keys())) remove(id);
  }

  setHotspots(hotspots);
  update();

  return {
    add,
    remove,
    setHotspots,
    update,
    destroy,
    getElement(id) {
      return map.get(id)?.el || null;
    },
    getHotspot(id) {
      return map.get(id)?.data || null;
    },
  };
}